import { useState } from 'react';
import { WorkerPairingWizard } from '../outreach/WorkerPairingWizard';

// device-pairing widget — renders the WorkerPairingWizard driven from
// directive attrs (scalar, not base64). The agent has already called
// pair-init and supplies the one-time token; the wizard advances through
// the install/scan/confirm steps locally via setStep on each next click.
//
// Prop names match the widget directive attrs the PairDevice tool emits:
//   :::widget[device-pairing]{token="..." projectorUrl="..." step="1"}:::
// (the tool always emits step="1"; the `stepProp ?? '5'` fallback only
// applies in tests / debug renders that omit the attr, where step 5
// shows the token immediately for inspection.)

export interface DevicePairingProps {
  token?: string;
  projectorUrl?: string;
  step?: string;
}

export function DevicePairing({ token: tokenProp, projectorUrl, step: stepProp }: DevicePairingProps) {
  // Parse step from attr string; default to 5 (token already generated).
  const initialStep = Math.min(6, Math.max(1, parseInt(stepProp ?? '5', 10))) as 1 | 2 | 3 | 4 | 5 | 6;
  const [step, setStep] = useState<1 | 2 | 3 | 4 | 5 | 6>(initialStep);
  // token is pre-supplied by the tool; treat it as immutable in the widget.
  const [token] = useState<string | null>(tokenProp ?? null);

  if (!token) {
    return (
      <p className="text-sm text-foreground-mute">
        No pairing token provided. Use <span className="font-mono">/pair</span> to generate one.
      </p>
    );
  }

  return (
    <WorkerPairingWizard
      step={step}
      token={token}
      projectorUrl={projectorUrl ?? window.location.origin}
      // Token was generated by the agent's PairDevice tool call; the widget
      // doesn't need to re-generate it. onGenerateToken is a no-op here.
      onGenerateToken={() => {}}
      onNextStep={() => setStep((prev) => Math.min(6, prev + 1) as 1 | 2 | 3 | 4 | 5 | 6)}
    />
  );
}
